// Singleton UserManager for the PCS IdP (oidc-client-ts). Centralizes the OIDC
// browser flow — Authorization Code + PKCE, token storage, silent refresh — so the
// rest of the app touches identity only through useAuth() and this small surface.
//
// Everything here is a no-op when OIDC is disabled (REACT_APP_AUTH_MODE != "oidc"),
// so importing it from the shared API client is safe in dummy-login mode.
import { UserManager, WebStorageStateStore } from 'oidc-client-ts';
import { OIDC_ENABLED, OIDC_STORAGE_KEY, oidcConfig } from './oidcConfig';

let manager = null;

/** Lazily build the UserManager (only when OIDC is enabled). */
export function getUserManager() {
  if (!OIDC_ENABLED) return null;
  if (!manager) {
    manager = new UserManager({
      ...oidcConfig,
      // Persist to localStorage so the session survives a refresh AND the API client
      // can read the access token synchronously (see getAccessTokenSync).
      userStore: new WebStorageStateStore({ store: window.localStorage }),
    });
  }
  return manager;
}

/** Redirect the browser to the IdP login page. `args` may carry { state }. */
export function signinRedirect(args) {
  return getUserManager().signinRedirect(args);
}

/** Complete the PKCE code exchange on the /auth/callback route; resolves to the User. */
export function handleSigninCallback() {
  return getUserManager().signinRedirectCallback();
}

/**
 * RP-initiated logout: redirect to the IdP end-session endpoint so the IdP's SSO
 * session is actually ended (a local-only token clear can't do this — the session
 * cookie lives on the IdP domain, so the IdP would silently log the same user back
 * in). oidc-client-ts sends `id_token_hint` from the stored user automatically (which
 * lets the IdP skip its logout confirmation prompt) and removes the local user, then
 * redirects back to `post_logout_redirect_uri` (/login). No-op when OIDC is disabled.
 */
export function signoutRedirect() {
  const mgr = getUserManager();
  return mgr ? mgr.signoutRedirect() : Promise.resolve();
}

/**
 * Synchronous access-token read for the axios request interceptor. Reads the
 * persisted oidc-client-ts user straight from localStorage (no async). Returns
 * null when signed out, expired, or OIDC is disabled.
 */
export function getAccessTokenSync() {
  if (!OIDC_ENABLED) return null;
  try {
    const raw = window.localStorage.getItem(OIDC_STORAGE_KEY);
    if (!raw) return null;
    const user = JSON.parse(raw);
    // expires_at is epoch seconds; treat an expired token as no token.
    if (user.expires_at && user.expires_at * 1000 <= Date.now()) return null;
    return user.access_token || null;
  } catch {
    return null;
  }
}
